import { useState } from 'react';
import { RavenSelect, RavenTextField } from '@ravenappdev/raven-ui';
import {
  SET_PRESSURE_MODIFIERS,
  SET_PRESSURE_UNITS,
  formatSetPressure,
  isSetPressureNumber,
  parseSetPressure,
  type SetPressureModifier,
  type SetPressureUnit,
} from '../utils/setPressure';

interface SetPressureFieldProps {
  /** The stored string; empty when the valve has no set pressure. */
  value: string;
  onChange: (next: string) => void;
  readOnly?: boolean;
}

interface SetPressureDraft {
  value: string;
  unit: SetPressureUnit | '';
  modifier: SetPressureModifier;
}

const UNIT_OPTIONS = SET_PRESSURE_UNITS.map((unit) => ({ value: unit, label: unit }));
const MODIFIER_LABELS: Record<SetPressureModifier, string> = {
  '': 'none',
  g: 'gauge',
  a: 'absolute',
  d: 'differential',
};
const MODIFIER_OPTIONS = (['', ...SET_PRESSURE_MODIFIERS] as const).map((modifier) => ({
  value: modifier,
  label: MODIFIER_LABELS[modifier],
}));

// The editor dock is about 310px wide. At the theme's 16px input font, selects
// wide enough for "mm H2O" and "differential" leave the number too narrow for
// "42.8", so the row's input text is 14px. Each select is sized to show its
// longest option in full; the number takes the rest of the row.
const ROW_INPUT_TEXT = { '&& .MuiInputBase-input': { fontSize: 14 } };
const UNIT_SELECT_WIDTH = 110;
const MODIFIER_SELECT_WIDTH = 122;

/** A stored value outside the grammar leaves the three fields empty. */
function draftFromStored(stored: string): SetPressureDraft {
  return parseSetPressure(stored) ?? { value: '', unit: '', modifier: '' };
}

/**
 * The set pressure as a number, a unit and a modifier, written back as the one
 * stored string. A stored value outside the grammar stays on screen as text until
 * the three fields hold a complete entry, which replaces it.
 */
export default function SetPressureField({ value, onChange, readOnly = false }: SetPressureFieldProps) {
  const [draft, setDraft] = useState<SetPressureDraft>(() => draftFromStored(value));
  // The last stored value the draft reflects. A value that differs came from
  // outside (a restore, another instrument), so the draft is re-read from it.
  const [reflectedValue, setReflectedValue] = useState(value);
  if (value !== reflectedValue) {
    setReflectedValue(value);
    setDraft(draftFromStored(value));
  }

  const isNonCanonical = value !== '' && parseSetPressure(value) === null;
  const isNumberInvalid = draft.value !== '' && !isSetPressureNumber(draft.value);

  const updateDraft = (patch: Partial<SetPressureDraft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    // A partial number ("1.", "-") or a missing unit writes nothing yet, so the
    // stored value holds until the entry is complete.
    let stored: string | null = null;
    if (next.value === '') stored = '';
    else if (isSetPressureNumber(next.value) && next.unit !== '') {
      stored = formatSetPressure({ value: next.value, unit: next.unit, modifier: next.modifier });
    }
    if (stored !== null && stored !== value) {
      setReflectedValue(stored);
      onChange(stored);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {isNonCanonical && (
        <div
          style={{
            padding: '6px 8px',
            borderRadius: 'var(--raven-radius-field)',
            border: '1px solid var(--raven-divider)',
            background: 'var(--raven-surface-tier2)',
          }}
        >
          <div style={{ fontFamily: 'var(--raven-font-mono)' }}>{value}</div>
          <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)' }}>
            Not in the standard form — re-enter it
          </div>
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <RavenTextField
          label="Value"
          size="small"
          value={draft.value}
          onChange={(event) => updateDraft({ value: event.target.value })}
          error={isNumberInvalid}
          disabled={readOnly}
          slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          sx={{ ...ROW_INPUT_TEXT, flex: 1, minWidth: 0 }}
        />
        <RavenSelect
          label="Unit"
          size="small"
          value={draft.unit}
          options={UNIT_OPTIONS}
          onChange={(event) => updateDraft({ unit: event.target.value as SetPressureUnit })}
          disabled={readOnly}
          sx={{ ...ROW_INPUT_TEXT, flex: `0 0 ${UNIT_SELECT_WIDTH}px` }}
        />
        <RavenSelect
          label="Modifier"
          size="small"
          value={draft.modifier}
          options={MODIFIER_OPTIONS}
          onChange={(event) => updateDraft({ modifier: event.target.value as SetPressureModifier })}
          disabled={readOnly}
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          sx={{ ...ROW_INPUT_TEXT, flex: `0 0 ${MODIFIER_SELECT_WIDTH}px` }}
        />
      </div>
    </div>
  );
}
